import type { Dispatch, SetStateAction } from 'react';
import { forwardRef } from 'react';

import { classNames } from '@/utils/classNames';

// Icon
import Icon from '@/components/base/Icon';
import type { InputProps } from '@/components/base/Input';
import Input from '@/components/base/Input';

export type DatepickerInputProps = Pick<
    InputProps,
    'noBorder' | 'noBorderShadow' | 'placeholder'
> & {
    toggleDatepicker: () => Promise<void>;
    setReferenceElement: Dispatch<SetStateAction<HTMLButtonElement>>;
    isLeftIcon?: boolean;
    isDisabled?: boolean;
};

const DatepickerInput = forwardRef<HTMLInputElement, DatepickerInputProps>(
    (
        {
            toggleDatepicker,
            setReferenceElement,
            isLeftIcon,
            placeholder,
            noBorder = true,
            noBorderShadow = false,
            isDisabled
        },
        ref
    ) => {
        return (
            <button
                ref={setReferenceElement}
                type="button"
                disabled={isDisabled}
                className={classNames(
                    'w-full border-none',
                    'focus-within:outline-none focus-within:ring-2 focus-within:ring-icoBlue-200 focus-within:ring-offset-0',
                    'disabled:cursor-not-allowed disabled:focus-within:ring-0'
                )}
                onClick={toggleDatepicker}>
                <Input
                    ref={ref}
                    readOnly
                    disabled={isDisabled}
                    noBorder={noBorder}
                    data-testid="datepicker-input"
                    id="datepicker"
                    noBorderShadow={noBorderShadow}
                    placeholder={placeholder}
                    className={classNames('cursor-pointer', {
                        'cursor-not-allowed': isDisabled
                    })}
                    inputStyle="datepicker"
                    beforeInput={
                        isLeftIcon ? (
                            <div className="leading-none">
                                <Icon
                                    icon="calendar"
                                    className="text-base text-icoGray-500 dark:text-icoDarkMode-wolf"
                                />
                            </div>
                        ) : null
                    }
                    afterInput={
                        isDisabled ? null : (
                            <div className="leading-none">
                                <Icon
                                    icon="chevron-bottom"
                                    className="align-middle text-base text-icoGray-500 dark:text-icoDarkMode-wolf"
                                />
                            </div>
                        )
                    }
                />
            </button>
        );
    }
);

DatepickerInput.displayName = 'DatepickerInput';

export default DatepickerInput;
